import React from 'react';
import { useIntl } from 'react-intl';
import { useT } from '@transifex/react';
import {
  ArchivedTabs,
  AvatarCollection,
  AwardGallery,
  AwardGalleryProps,
  ClickableCardsProps,
  CmsCallToAction,
  CmsCallToActionProps,
  ContentColumn,
  ContentColumnProps,
  ContentColumnsProps,
  ContentColumnsSlider,
  ContentColumnsSliderProps,
  CtaBlock,
  CtaBlockProps,
  CustomersReviews,
  CustomersReviewsProps,
  DealsSlider,
  DealsSliderProps,
  FAQ,
  FAQContentProps,
  HeroBrandEvolutionProps,
  HeroProps,
  IppPortfolioTiles,
  IppPortfolioTilesProps,
  IppPrognoseChart,
  IppPrognoseChartProps,
  IppRiskClasses,
  PageHero,
  PageHeroProps,
  PageSummary,
  PageSummaryProps,
  PageTitle,
  PageTitleProps,
  PressContactCard,
  PressContactCardProps,
  PressMediaArticleCard,
  PressMediaArticleCardProps,
  PressReleaseArticle,
  PressReleaseArticleProps,
  PressReleaseHeadline,
  PressReleaseHeadlineProps,
  PressReleaseImage,
  PressReleaseImageProps,
  PressReleaseQuote,
  PressReleaseQuoteProps,
  ProductCategoryTilesProps,
  RelatedArticles,
  RelatedArticlesProps,
  Table,
  TableOfContents,
  TableOfContentsProps,
  TableProps,
  TabsComponentProps,
  TrustBar,
  TrustBarProps,
  useRichTextEditor,
  USPImages,
  USPImagesProps,
  VideoPlayer,
  VideoPlayerProps,
} from '@raisin/components-lib';
import type { ModelPortfolios } from '@raisin/investments-data/api/contentStack/types';
import { ClickableCards } from 'components/ClickableCards';
import { ContentColumns } from 'components/ContentColumns';
import { CopyWithIcons } from 'components/CopyWithIcons';
import { Hero } from 'components/Hero';
import { HeroBrandEvolution } from 'components/HeroBrandEvolution';
import { InternalErrorDisplay } from 'components/InternalErrorDisplay';
import LayoutContainer, { LayoutContainerProps } from 'components/LayoutContainer';
import { MediaTile } from 'components/MediaTile';
import { Newsletter, NewsletterProps } from 'components/Newsletter';
import { OffersTable } from 'components/OffersTable';
import { ProductCards } from 'components/ProductCards';
import { ProductCategories } from 'components/ProductCategories';
import { ProductCategoryProps } from 'components/ProductCategories/types';
import { ProductCategoryTiles } from 'components/ProductCategoryTiles';
import { ProductOfferTable } from 'components/ProductOfferTable';
import PrognoseChartIPP from 'components/PrognoseChartIPP';
import { USProductOffer } from 'types';
import messages from '../messages';
import { OffersTableProps } from './offersTableConfigs';

export const components = {
  avatar_collection: AvatarCollection,
  award_gallery: (props: AwardGalleryProps) => <AwardGallery {...props} />,
  page_title: (props: PageTitleProps) => <PageTitle {...props} />,
  trust_bar: (props: TrustBarProps) => <TrustBar {...props} />,
  page_hero: (props: PageHeroProps) => <PageHero {...props} />,
  content_column: (props: ContentColumnProps) => <ContentColumn {...props} />,
  hero: (props: HeroProps) => <Hero {...props} />,
  faq: (props: FAQContentProps) => <FAQ {...props} />,
  cta: (props: CmsCallToActionProps) => <CmsCallToAction {...props} />,
  content_columns: (props: ContentColumnsProps) => <ContentColumns {...props} />,
  clickable_cards: (props: ClickableCardsProps) => <ClickableCards {...props} />,
  cta_block: (props: CtaBlockProps) => <CtaBlock {...props} />,
  table_of_contents: (props: TableOfContentsProps) => <TableOfContents {...props} />,
  customers_reviews: (props: CustomersReviewsProps) => <CustomersReviews {...props} />,
  portfolio_tiles: (props: IppPortfolioTilesProps) => <IppPortfolioTiles {...props} />,
  content_columns_slider: (props: ContentColumnsSliderProps) => <ContentColumnsSlider {...props} />,
  deals_slider: (props: DealsSliderProps) => <DealsSlider {...props} />,
  page_summary: (props: PageSummaryProps) => <PageSummary {...props} />,
  offers_table: (props: OffersTableProps) => <OffersTable {...props} />,
  us_product_offer_table: (
    componentData,
    _formatMessage,
    _modelPortfolios,
    contentEntries: Record<string, any>,
    usProductOffers: Array<USProductOffer>,
  ) => (
    <ProductOfferTable
      productOffers={usProductOffers}
      bankId={componentData.bank_id}
      productId={componentData.product_id}
      bankLogoLinks={contentEntries.bank_logo_links}
      featuredProducts={componentData.featured_products}
      inactiveProducts={contentEntries?.inactive_products?.[0]?.products}
      categoryTabsToShow={componentData.displayed_offer_tabs}
      financialInstitutions={contentEntries?.financial_institutions}
      showFinancialInstitutionsDropdown={componentData.show_financial_institutions_dropdown}
    />
  ),
  layout_container: (props: LayoutContainerProps) => <LayoutContainer {...props} />,
  related_articles: (props: RelatedArticlesProps, formatMessage: Function) => (
    <RelatedArticles {...props} loadMoreLabel={formatMessage(messages.RelatedArticlesShowMore)} />
  ),
  prognose_chart: (props: IppPrognoseChartProps, formatMessage: Function) => {
    // eslint-disable-next-line
    const { formatNumber } = useIntl();
    // eslint-disable-next-line
    const t = useT();
    // eslint-disable-next-line
    const prognoseChartInputsParagraph = useRichTextEditor(
      t(messages.PrognoseChartInputsModalParagraph),
      {
        a: () => <a href="https://www.weltsparen.de/investieren/risikohinweise"></a>,
      },
    );
    const translations = {
      prognoseChartHeading: formatMessage(messages.PrognoseChartHeading),
      inputs: {
        prognoseChartInputsBoxHeading: formatMessage(messages.PrognoseChartHeading),
        prognoseChartInputsBoxOneTimeInvestmentLabel: formatMessage(
          messages.PrognoseChartInputsOneTimeInvestmentLabel,
        ),
        prognoseChartInputsBoxOneTimeInvestmentPlaceholder: formatMessage(
          messages.PrognoseChartInputsOneTimeInvestmentPlaceholder,
        ),
        prognoseChartInputsBoxOneTimeInvestmentHelperMin: formatMessage(
          messages.PrognoseChartInputsOneTimeInvestmentHelperMin,
        ),
        prognoseChartInputsBoxOneTimeInvestmentHelperMax: formatMessage(
          messages.PrognoseChartInputsOneTimeInvestmentHelperMax,
        ),
        prognoseChartInputsBoxMonthlySavingRateLabel: formatMessage(
          messages.PrognoseChartInputsMonthlySavingRateLabel,
        ),
        prognoseChartInputsBoxMonthlySavingRatePlaceholder: formatMessage(
          messages.PrognoseChartInputsMonthlySavingRatePlaceholder,
        ),
        prognoseChartInputsBoxMonthlySavingRateHelperMin: formatMessage(
          messages.PrognoseChartInputsMonthlySavingRateHelperMin,
        ),
        prognoseChartInputsBoxMonthlySavingRateHelperMax: formatMessage(
          messages.PrognoseChartInputsMonthlySavingRateHelperMax,
        ),
        prognoseChartInputsBoxPortfolioLabel: formatMessage(
          messages.PrognoseChartInputsPortfolioLabel,
        ),
        prognoseChartInputsBoxPortfolioPlaceholder: formatMessage(
          messages.PrognoseChartInputsPortfolioPlaceholder,
        ),
        prognoseChartInputsBoxPortfolioValue30: formatMessage(
          messages.PrognoseChartInputsPortfolioValue30,
        ),
        prognoseChartInputsBoxPortfolioValue50: formatMessage(
          messages.PrognoseChartInputsPortfolioValue50,
        ),
        prognoseChartInputsBoxPortfolioValue70: formatMessage(
          messages.PrognoseChartInputsPortfolioValue70,
        ),
        prognoseChartInputsBoxPortfolioValue100: formatMessage(
          messages.PrognoseChartInputsPortfolioValue100,
        ),
        prognoseChartInputsBoxNotesHeader: formatMessage(messages.PrognoseChartInputsNotesHeader),
        prognoseChartInputsBoxNotesParagraph: formatMessage(
          messages.PrognoseChartInputsNotesParagraph,
        ),
        prognoseChartInputsBoxOpenModalCTA: formatMessage(messages.PrognoseChartInputsOpenModalCTA),
        prognoseChartInputsBoxModalHeading: formatMessage(messages.PrognoseChartInputsModalHeading),
        prognoseChartInputsBoxModalParagraph: prognoseChartInputsParagraph,
        prognoseChartInputsBoxModalCTA: formatMessage(messages.PrognoseChartInputsModalCta),
        formatNumber: formatNumber,
      },
      chartBox: {
        prognoseChartBoxPercentNote: formatMessage(messages.PrognoseChartBoxPercentNote),
        prognoseChartBoxHeading: formatMessage(messages.PrognoseChartBoxHeading),
        prognoseChartBoxDuration1: formatMessage(messages.PrognoseChartBoxDuration1),
        prognoseChartBoxDuration3: formatMessage(messages.PrognoseChartBoxDuration3),
        prognoseChartBoxDuration2: formatMessage(messages.PrognoseChartBoxDuration2),
        PrognoseChartBoxErrorMsg: formatMessage(messages.PrognoseChartBoxErrorMsg),
        prognoseChartBoxNote: formatMessage(messages.PrognoseChartBoxNote),
        prognoseChartBoxLegend1: formatMessage(messages.PrognoseChartBoxLegend1),
        prognoseChartBoxLegend2: formatMessage(messages.PrognoseChartBoxLegend2),
        prognoseChartBoxLegend3: formatMessage(messages.PrognoseChartBoxLegend3),
        prognoseChartBoxLegend4: formatMessage(messages.PrognoseChartBoxLegend4),
      },
    } as IppPrognoseChartProps;
    return <IppPrognoseChart {...props} {...translations} />;
  },
  newsletter: (props: NewsletterProps) => <Newsletter {...props} />,
  tabs: (props: TabsComponentProps, formatMessage: Function) => (
    <ArchivedTabs {...props} closeText={formatMessage(messages.GlobalClose)} />
  ),
  risk_classes: (_props, formatMessage: Function, modelPortfolios: ModelPortfolios) => (
    <IppRiskClasses
      modelPortfolios={modelPortfolios}
      portfolioDetailsMessages={{
        riskHead: formatMessage(messages.riskHead),
        riskParagraph: formatMessage(messages.riskParagraph),
        portfolioDetailsModalTitle: formatMessage(messages.portfolioDetailsModalTitle),
        securityDocumentsModalTitle: formatMessage(messages.securityDocumentsModalTitle),
        securityDetailsTitle: formatMessage(messages.securityDetailsTitle),
        securityDetailsHead1: formatMessage(messages.securityDetailsHead1),
        securityDetailsHead2: formatMessage(messages.securityDetailsHead2),
        ISIN: formatMessage(messages.ISIN),
        portfolioInfoTitle: formatMessage(messages.portfolioInfoTitle),
        portfolioInfoDetails: formatMessage(messages.portfolioInfoDetails),
        highRisk: formatMessage(messages.highRisk),
        lowRisk: formatMessage(messages.lowRisk),
        fees: formatMessage(messages.fees),
        fundCost: formatMessage(messages.fundCost),
        targetReturn: formatMessage(messages.targetReturn),
        maximumDrawdown: formatMessage(messages.maximumDrawdown),
        ETF: formatMessage(messages.ETF),
        funds: formatMessage(messages.funds),
        portfolio100: formatMessage(messages.portfolio100),
        portfolio70: formatMessage(messages.portfolio70),
        portfolio50: formatMessage(messages.portfolio50),
        portfolio30: formatMessage(messages.portfolio30),
        portfolioMM: formatMessage(messages.portfolioMM),
        portfolioCaption100: formatMessage(messages.portfolioCaption100),
        portfolioCaption70: formatMessage(messages.portfolioCaption70),
        portfolioCaption50: formatMessage(messages.portfolioCaption50),
        portfolioCaption30: formatMessage(messages.portfolioCaption30),
        portfolioCaptionMM: formatMessage(messages.portfolioCaptionMM),
        portfolioParagraph100: formatMessage(messages.portfolioParagraph100),
        portfolioParagraph70: formatMessage(messages.portfolioParagraph70),
        portfolioParagraph50: formatMessage(messages.portfolioParagraph50),
        portfolioParagraph30: formatMessage(messages.portfolioParagraph30),
        portfolioParagraphMM: formatMessage(messages.portfolioParagraphMM),
        cta: formatMessage(messages.cta),
        new: formatMessage(messages.new),
      }}
    />
  ),
  ipp_prognose_chart: () => <PrognoseChartIPP />,
  product_cards: (props) => <ProductCards {...props} />,
  product_categories: (props: ProductCategoryProps) => <ProductCategories {...props} />,
  copy_with_icons: CopyWithIcons,
  table: (props: TableProps) => <Table {...props} />,
  press_contact_card: (props: PressContactCardProps) => <PressContactCard {...props} />,
  press_release_headline: (props: PressReleaseHeadlineProps) => <PressReleaseHeadline {...props} />,
  press_release_quote: (props: PressReleaseQuoteProps) => <PressReleaseQuote {...props} />,
  media_tile: MediaTile,
  internal_error: InternalErrorDisplay,
  press_release_image: (props: PressReleaseImageProps) => <PressReleaseImage {...props} />,
  press_release_article: (props: PressReleaseArticleProps) => <PressReleaseArticle {...props} />,
  press_media_article_card: (props: PressMediaArticleCardProps) => (
    <PressMediaArticleCard {...props} />
  ),
  hero_brand_evolution: (props: HeroBrandEvolutionProps) => <HeroBrandEvolution {...props} />,
  video_player: (props: VideoPlayerProps) => <VideoPlayer {...props} />,
  product_category_tiles: (props: ProductCategoryTilesProps) => <ProductCategoryTiles {...props} />,
  empty_reference: () => <span />,
  usp_images: (props: USPImagesProps) => <USPImages {...props} />,
};

export const getComponent = (key: keyof typeof components) => {
  const component = components[key];
  if (!component) {
    // eslint-disable-next-line no-console
    console.error(`The ${key} component is not defined.`);
    return null;
  }
  return component;
};
